<template>
  <div
    v-if="dsComponentDetails.version === VersionOptions.LEGACY && hasAppendOrPrependText"
    class="input-container"
    data-sb-regression="sb-text-number-input"
    :class="state"
    @click="focusInput"
  >
    <span
      v-if="hasPrependText"
      class="prepend"
    >
      <!--
        @slot text, components, or HtmlElements as children of the component
       -->
      <slot name="prepend">
        {{ prepend }}
      </slot>
    </span>
    <input
      :id="internalId"
      v-bind="attrs"
      :class="classes"
      :placeholder="placeholder"
      :type="type"
      :max="max"
      :min="min"
      :step="step"
      v-on="attrs"
      @input="inputChanged"
      @blur="handleBlur"
      @focus="handleFocus"
    >
    <span
      v-if="hasAppendText"
      class="append"
    >
      <!--
        @slot text, components, or HtmlElements as children of the component
       -->
      <slot name="append">
        {{ append }}
      </slot>
    </span>
  </div>
  <input
    v-else-if="dsComponentDetails.version === VersionOptions.LEGACY && !hasAppendOrPrependText"
    :id="internalId"
    v-bind="attrs"
    :class="classes"
    :placeholder="placeholder"
    :type="type"
    :max="max"
    :min="min"
    :step="step"
    v-on="attrs"
    @input="inputChanged"
    @blur="handleBlur"
    @focus="handleFocus"
  >
  <SbNumberInputNext
    v-else-if="dsComponentDetails.version === VersionOptions.NEXT"
    :id="internalId"
    v-bind="attrs"
    v-model="attrs.value"
    :class="classes"
    :placeholder="placeholder"
    :type="type"
    :max="max"
    :min="min"
    :step="step"
    :locale="locale"
    :min-fraction-digits="numberType === 'decimal' ? minFractionDigits : null"
    :max-fraction-digits="numberType === 'decimal' ? maxFractionDigits : null"
    :prefix="prepend"
    :suffix="append"
    @input="inputChanged"
  />
</template>

<script>
import fieldHelper from '@src/utils/mixins/fieldHelperMixin';
import numberFieldHelper from '@src/utils/mixins/numberFieldHelperMixin';
import labelHelper from '@src/utils/mixins/labelHelperMixin';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';
import SbNumberInputNext from '@src/components/Input/SbNumberInputNext.vue';

/**
 * Text Inputs allows users to input text. :)
 */
export default {
  name: 'SbNumberInput',
  components: {
    SbNumberInputNext,
  },
  mixins: [labelHelper, fieldHelper, numberFieldHelper, versionedComponentsMixin],
  props: {
    /**
     * String to be used as the input placeholder text.
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * String to be appended to the input box.
     * This prop text is actually used as the default value of a named slot: `append`.
     * If for some reason you have some special html markup you would like to append to the input box in leiu of a simple `span` with text,
     * eg. `<p slot="append">my appended text</p>`.
     * you can use the named slot.
     */
    append: {
      type: String,
      default: '',
    },
    /**
     * String to be prepended to the input box.
     * This prop text is actually used as the default value of a named slot: `prepend`.
     * If for some reason you have some special html markup you would like to append to the input box in leiu of a simple `span` with text,
     * eg. `<p slot="prepend">my prepended text</p>`.
     * you can use the named slot.
     */
    prepend: {
      type: String,
      default: '',
    },
    /**
     * Text alignment of the input box. Can be `left|center|right`
     */
    align: {
      type: String,
      default: 'left',
      validator: (value) => {
        return value.match(/(left|center|right)/);
      },
    },
    /**
     * Type of number input. Can be `number`
     */
    type: {
      type: String,
      default: 'number',
      validator: (value) => {
        return value.match(/(number)/);
      },
    },
    /**
     * Type of number input.
     */
    numberType: {
      type: String,
      default: 'decimal',
      validator: (value) => {
        return value.match(/(decimal|wholeNumber)/);
      },
    },
    /**
     * html id of the input element. default will be set as the Vue component uuid.
     */
    id: {
      type: String,
      default: null,
    },
    value: {
      type: Number,
      required: false,
      default: null,
    },
    /**
     * If you are using this input as part of the SbField pattern with SbFieldHelperText, counter is used to set the upper limit of the Field.
     * *This does not actually set a maxlength property on the input. It is only used as a visual inside the SbFieldHelperText.*
     */
    counter: {
      type: Number,
      default: null,
    },
    /**
     * Manually trigger various states of the button.
     * `hover, active, focus`
     */
    state: {
      type: String,
      default: null,
      validator: (value) => value.match(/(focus)/),
    },
    /**
     * Max value of the number input
     */
    max: {
      type: Number,
      required: false,
      default: null,
    },
    /**
     * Min value of the number input
     */
    min: {
      type: Number,
      required: false,
      default: null,
    },
    /**
     * Step value of the number input
     */
    step: {
      type: Number,
      required: false,
      default: 1,
    },
    nullable: {
      type: Boolean,
      default: false,
    },
    /**
     * Max number of fraction digits. Ex: :maxFractionDigits="3" - 10.001
     */
    maxFractionDigits: {
      type: Number,
      default: 10,
    },
    /**
     * Min number of fraction digits. Ex: :minFractionDigits="2" - 10.00
     */
    minFractionDigits: {
      type: Number,
      default: 1,
    },
    /**
     * Locale to be used for number formatting. Ex: 'en-US', 'de-DE', 'fr-FR', etc.
     */
    locale: {
      type: String,
      default: 'en-US',
    },
  },
  emits: [
    'update:value',
    'set-label-for',
    'input',
  ],
  computed: {
    hasAppendText() {
      return this.append.length > 0 || (this.$slots.append && this.$slots.append());
    },
    hasPrependText() {
      return this.prepend.length > 0 || (this.$slots.prepend && this.$slots.prepend());
    },
    hasAppendOrPrependText() {
      return this.hasAppendText || this.hasPrependText;
    },
    classes() {
      const classList = ['sb-input'];
      if (this.align !== 'left') classList.push(`sb-input-${this.align}`);
      if (this.state !== null) classList.push(this.state);
      return classList;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@src/components/Input/style/sb-text-input.scss';
</style>
